import { config } from "@/shared/lib/config";
import { uploadFileToPresignedUrl } from "@/shared/lib/upload-to-presigned";
import type { PublicFormHttpError, PublicFormResponseDTO } from "./types";

async function parseJsonError(res: Response): Promise<PublicFormHttpError> {
  let code = "UNKNOWN";
  let fieldId: string | undefined;
  try {
    const raw: unknown = await res.json();
    if (raw !== null && typeof raw === "object") {
      if (
        "error" in raw &&
        typeof (raw as { error: unknown }).error === "string"
      ) {
        code = (raw as { error: string }).error;
      }
      const fid = (raw as { fieldId?: unknown }).fieldId;
      if (typeof fid === "string") fieldId = fid;
    }
  } catch {
    /* omit */
  }
  return { httpStatus: res.status, code, fieldId };
}

function formUrl(id: string) {
  return `${config.apiUrl}/public/forms/${encodeURIComponent(id)}`;
}

export const publicFormsApi = {
  async get(id: string): Promise<PublicFormResponseDTO> {
    const res = await fetch(formUrl(id), { credentials: "include" });
    if (!res.ok) throw await parseJsonError(res);
    return (await res.json()) as PublicFormResponseDTO;
  },

  /** Presign ສາຍ public — ບໍ່ຜ່ານ /upload ທີ່ຕ້ອງເຂົ້າລະບົບ */
  async presign(
    formId: string,
    body: {
      fieldId: string;
      filename: string;
      contentType?: string;
      sizeBytes?: number;
    },
  ): Promise<{ uploadUrl: string; key: string }> {
    const res = await fetch(`${formUrl(formId)}/presign`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw await parseJsonError(res);
    return (await res.json()) as { uploadUrl: string; key: string };
  },

  async uploadFileViaPresign(formId: string, fieldId: string, file: File) {
    const { uploadUrl, key } = await publicFormsApi.presign(formId, {
      fieldId,
      filename: file.name,
      contentType: file.type || undefined,
      sizeBytes: file.size,
    });
    await uploadFileToPresignedUrl(uploadUrl, file, {
      contentType: file.type || undefined,
    });
    return key;
  },

  async submit(
    formId: string,
    body: {
      answers: Array<{ fieldId: string; value?: string; fileKey?: string }>;
    },
  ): Promise<{ ok: true; responseId: string }> {
    const res = await fetch(`${formUrl(formId)}/submit`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw await parseJsonError(res);
    return (await res.json()) as { ok: true; responseId: string };
  },
};

export function describePublicLoadError(code: string): string {
  switch (code) {
    case "FORM_NOT_FOUND":
      return "ບໍ່ພົບຟອມ ຫຼື ລິ້ງບໍ່ຖືກຕ້ອງ";
    case "FORM_NOT_AVAILABLE":
      return "ຟອມນີ້ຍັງບໍ່ເປີດໃຫ້ກອກ ຫຼື ປິດຖາວອນໃນເວລານີ້";
    case "FORM_DEADLINE_PASSED":
      return "ປິດຮັບຕາມກຳນົດແລ້ວ";
    default:
      return "ໂຫຼດຟອມບໍ່ສຳເລັດ ກະລຸນາລອງອີກຄັ້ງພາຍຫຼັງ";
  }
}

export function describePublicSubmitError(code: string): {
  title: string;
  detail?: string;
} {
  switch (code) {
    case "FORM_FULL":
      return {
        title: "ຮັບຄົບຈຳນວນແລ້ວ",
        detail: "ຟອມບໍ່ເປີດຮັບຄຳຕອບເພີ່ມໃນເວລານີ້",
      };
    case "FORM_DEADLINE_PASSED":
      return {
        title: "ປິດກຳນົດຮັບແລ້ວ",
        detail: "",
      };
    case "FORM_NOT_AVAILABLE":
      return {
        title: "ຟອມບໍ່ເປີດໃຫ້ໃຊ້ງານ",
        detail: "",
      };
    case "REQUIRED_MISSING":
      return {
        title: "ຂໍ້ມູນທີ່ຈຳເປັນບໍ່ຄົບ",
        detail: "",
      };
    default:
      return {
        title: "ສົ່ງບໍ່ສຳເລັດ",
        detail: code === "UNKNOWN" ? undefined : `ກວດຂໍ້ມູນແລ້ວລອງໃໝ່ (${code})`,
      };
  }
}

/** ຂໍ້ຄວາມສັ້ນໃຕ້ຟິວ (public submit / validation) */
export function publicSubmitFieldMessage(code: string): string {
  switch (code) {
    case "REQUIRED_MISSING":
      return "ກະລຸນາກອກຫຼືແນບຂໍ້ມູນໃຫ້ຄົບ";
    case "INVALID_DATE":
      return "ຮູບແບບວັນທີບໍ່ຖືກຕ້ອງ";
    case "INVALID_DATETIME":
      return "ຮູບແບບວັນເວລາບໍ່ຖືກຕ້ອງ";
    case "OPTION_OUT_OF_RANGE":
      return "ຄ່າທີ່ເລືອກບໍ່ຢູ່ໃນລາຍການທີ່ອະນຸຍາດ";
    case "INVALID_RATING":
      return "ຄະແນນບໍ່ຖືກຕ້ອງ";
    case "INVALID_NUMBER":
      return "ຮູບແບບຕົວເລກບໍ່ຖືກຕ້ອງ";
    case "NUMBER_NOT_INTEGER":
      return "ຮັບສະເພາະຈຳນວນເຕັມເທົ່ານັ້ນ";
    case "NUMBER_OUT_OF_RANGE":
      return "ຄ່າຢູ່ນອກຊ່ວງທີ່ອະນຸຍາດ";
    case "TYPE_MISMATCH_VALUE_FILE":
      return "ຂໍ້ມູນຟິວນີ້ບໍ່ສາມາດມີທັງຂໍ້ຄວາມແລະໄຟລ";
    case "FILE_KEY_PREFIX_INVALID":
      return "ໄຟລອັບໂຫຼດບໍ່ຖືກຕ້ອງ ກະລຸນາອັບໂຫຼດໃໝ່";
    case "UNKNOWN_FIELD":
      return "ຟິວນີ້ບໍ່ມີໃນຟອມ";
    case "UNSUPPORTED_FIELD_TYPE":
      return "ປະເພດຟິວນີ້ບໍ່ຮອງຮັບ";
    case "DUAL_VALUE_AND_FILE":
      return "ສົ່ງຄ່າຊ້ອນກັນບໍ່ຖືກຕ້ອງ (ຟິວດຽວກັນ)";
    case "DUPLICATE_FIELD_ANSWER":
      return "ມີຄຳຕອບຊ້ຳສຳລັບຟິວດຽວກັນ";
    case "REPEATER_TOO_FEW_ITEMS":
      return "ຈຳນວນລາຍການບໍ່ຄົບຕາມທີ່ກຳນົດ";
    case "REPEATER_TOO_MANY_ITEMS":
      return "ຈຳນວນລາຍການເກີນທີ່ອະນຸຍາດ";
    case "REPEATER_INVALID_JSON":
    case "REPEATER_SETTINGS_INVALID":
      return "ຂໍ້ມູນລາຍການບໍ່ຖືກຕ້ອງ";
    case "REPEATER_PATTERN_MISMATCH":
      return "ຮູບແບບຂໍ້ມູນບໍ່ຖືກຕ້ອງ";
    case "REPEATER_TEXT_TOO_SHORT":
      return "ຂໍ້ຄວາມສັ້ນເກີນໄປ";
    case "REPEATER_TEXT_TOO_LONG":
      return "ຂໍ້ຄວາມຍາວເກີນໄປ";
    default:
      return "ຂໍ້ມູນບໍ່ຖືກຕ້ອງ ກະລຸນາກວດແລ້ວລອງໃໝ່";
  }
}
